import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { pairAssetsWithText } from '../../lib/pairAssetsWithText';
import type { PublicPreviewDto, PublicPreviewResponse } from './publicDto';
import { FeedMockup } from './mockups/FeedMockup';
import { GoogleSearchMockup } from './mockups/GoogleSearchMockup';
import { StoryMockup } from './mockups/StoryMockup';
import { TextVariationsTab, textVariationsHasContent } from './TextVariationsTab';

// Ad Preview Mockups Stage 1 §4. Public preview page rendered on
// /preview/:token (outside SignedInGuard + AppShell — see src/App.tsx).
//
// Data fetch uses plain `fetch` instead of the authed `api` helper so
// no Authorization header is sent (franchisees aren't signed in).
// Lifecycle states map to dedicated full-page surfaces — not found /
// revoked / expired all render their own friendly message rather than
// dumping the JSON or showing a generic error.

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; status: 'not_found' | 'revoked' | 'expired' | 'http' }
  | { kind: 'ok'; dto: PublicPreviewDto };

async function fetchPreview(token: string): Promise<FetchState> {
  try {
    const res = await fetch(`/api/public/preview/${encodeURIComponent(token)}`, {
      // Public endpoint — no credentials, no Authorization. Browser
      // can still send cookies on same-origin requests, but the server
      // ignores them.
      credentials: 'omit',
    });
    if (res.status === 429) {
      return {
        kind: 'error',
        status: 'http',
        message:
          'Too many requests right now. Please wait a minute and refresh.',
      };
    }
    const body = (await res.json().catch(() => ({}))) as Partial<PublicPreviewResponse>;
    if (body && body.status === 'ok') {
      return { kind: 'ok', dto: body as PublicPreviewDto };
    }
    if (body && body.status === 'not_found') {
      return {
        kind: 'error',
        status: 'not_found',
        message: 'Preview not found.',
      };
    }
    if (body && body.status === 'revoked') {
      return {
        kind: 'error',
        status: 'revoked',
        message: 'This preview has been revoked. Please contact your agency.',
      };
    }
    if (body && body.status === 'expired') {
      const exp = (body as { expiresAt?: string }).expiresAt;
      return {
        kind: 'error',
        status: 'expired',
        message: exp
          ? `This preview expired on ${new Date(exp).toLocaleDateString()}. Contact your agency for an updated version.`
          : 'This preview has expired.',
      };
    }
    return {
      kind: 'error',
      status: 'http',
      message: 'Could not load preview.',
    };
  } catch {
    return {
      kind: 'error',
      status: 'http',
      message: 'Could not load preview. Check your connection and try again.',
    };
  }
}

type TabKey = 'story' | 'feed' | 'google' | 'text';

export function PreviewPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  // Default tab follows the tab order (Feed first per §13.6). The
  // auto-switch effect below falls back to the next enabled tab if
  // Feed has no content.
  const [tab, setTab] = useState<TabKey>('feed');

  useEffect(() => {
    if (!token) {
      setState({
        kind: 'error',
        status: 'not_found',
        message: 'Preview not found.',
      });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchPreview(token).then((s) => {
      if (!cancelled) setState(s);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Set the tab title for shared-link clarity. Falls back to a generic
  // string when state hasn't loaded yet.
  useEffect(() => {
    if (state.kind === 'ok') {
      document.title = `Ad Preview — ${state.dto.brand.name}`;
    } else {
      document.title = 'Ad Preview';
    }
  }, [state]);

  if (state.kind === 'loading') {
    return (
      <div className="min-h-screen bg-white">
        <div className="flex items-center justify-center pt-32">
          <CenteredSpinner />
        </div>
      </div>
    );
  }
  if (state.kind === 'error') {
    return <ErrorShell message={state.message} />;
  }

  return <PreviewLoaded dto={state.dto} tab={tab} onTab={setTab} />;
}

function ErrorShell({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="max-w-md mx-auto pt-24 px-4 text-center space-y-3">
        <h1 className="text-2xl font-semibold">Preview unavailable</h1>
        <p className="text-slate-600">{message}</p>
        <p className="text-xs text-slate-400 pt-6">Kyck</p>
      </div>
    </div>
  );
}

function PreviewLoaded({
  dto,
  tab,
  onTab,
}: {
  dto: PublicPreviewDto;
  tab: TabKey;
  onTab: (next: TabKey) => void;
}) {
  // Tab counts + enablement. Tabs with zero content are disabled with
  // a tooltip per §4.3.
  const hasMeta = !!dto.meta;
  const hasGoogle = !!dto.google;
  const storyCount = dto.meta?.storyAssets.length ?? 0;
  const feedCount = dto.meta?.feedAssets.length ?? 0;
  const googleEnabled =
    !!dto.google &&
    (dto.google.headlines.length > 0 || dto.google.descriptions.length > 0);
  // Stage 1 §13.12. Text Variations tab — hidden entirely when both
  // Meta + Google blocks have nothing to show; otherwise sits last in
  // the strip.
  const textEnabled = textVariationsHasContent(dto);
  // Tab order: Feed first, then Story, then Google, then Text
  // Variations. Feed leads (§13.6); Text Variations is appended last
  // because it's the "look up the raw copy" reference, not the
  // primary visual review surface.
  //
  // The Text Variations tab is the only one that gets HIDDEN
  // entirely when empty (other tabs stay visible but disabled so
  // the franchisee can see "Meta Feed (0)" and understand nothing
  // was authored for that format). Text Variations vs no-text-at-all
  // isn't a useful signal — if both Meta + Google blocks are empty,
  // the row has effectively no copy, and a "Text Variations (empty)"
  // tab adds noise.
  const tabs = useMemo(() => {
    type Tab = {
      key: TabKey;
      label: string;
      count: number | null;
      enabled: boolean;
    };
    // Only the channels the server put in the DTO get tabs: no empty tab for a
    // channel that is off for this location or campaign.
    const list: Tab[] = [];
    if (hasMeta) {
      list.push(
        {
          key: 'feed',
          label: 'Meta Feed',
          count: feedCount,
          enabled: feedCount > 0,
        },
        {
          key: 'story',
          label: 'Meta Story',
          count: storyCount,
          enabled: storyCount > 0,
        },
      );
    }
    if (hasGoogle) {
      list.push({
        key: 'google',
        label: 'Google Search',
        count: null,
        enabled: googleEnabled,
      });
    }
    if (textEnabled) {
      list.push({
        key: 'text',
        label: 'Text Variations',
        count: null,
        enabled: true,
      });
    }
    return list;
  }, [hasMeta, hasGoogle, storyCount, feedCount, googleEnabled, textEnabled]);

  // If the AM's chosen default tab is empty, switch to the first
  // populated one so the page doesn't open on a disabled tab.
  useEffect(() => {
    const current = tabs.find((t) => t.key === tab);
    if (current?.enabled) return;
    const first = tabs.find((t) => t.enabled);
    if (first) onTab(first.key);
  }, [tabs, tab, onTab]);

  if (!hasMeta && !hasGoogle) {
    return (
      <div className="min-h-screen bg-white text-slate-900">
        <PreviewHeader dto={dto} />
        <main className="max-w-2xl mx-auto px-4 pb-24 pt-10 text-center">
          <p className="text-sm text-slate-500">Nothing to review for this location.</p>
        </main>
        <PreviewFooter dto={dto} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <PreviewHeader dto={dto} />
      <PreviewTabs tabs={tabs} activeKey={tab} onSelect={onTab} />
      <main className="max-w-2xl mx-auto px-4 pb-24 pt-6">
        {tab === 'story' && (
          <TabEmpty visible={storyCount === 0} message="No Story assets typed for this campaign." />
        )}
        {tab === 'feed' && (
          <TabEmpty visible={feedCount === 0} message="No Feed assets typed for this campaign." />
        )}
        {tab === 'google' && (
          <TabEmpty visible={!googleEnabled} message="No Google ad content for this campaign." />
        )}
        {/* Mockup components are added one per commit (Story / Feed /
            Google). Each tab renders its own stack of mockups when a
            component is wired in. */}
        {tab === 'story' && storyCount > 0 && (
          <StoryStack dto={dto} />
        )}
        {tab === 'feed' && feedCount > 0 && (
          <FeedStack dto={dto} />
        )}
        {tab === 'google' && googleEnabled && dto.google && (
          <GoogleSearchMockup
            google={dto.google}
            brandName={dto.brand.name}
          />
        )}
        {tab === 'text' && textEnabled && <TextVariationsTab dto={dto} />}
      </main>
      <PreviewFooter dto={dto} />
    </div>
  );
}

// Stage 1 §13.11. Story + Feed stacks pair each asset with one of the
// Campaign's headline/body variations via pairAssetsWithText. The
// pairing is deterministic per asset id so a franchisee opening the
// preview twice sees identical mockups. Same pool drives both Story
// and Feed — there's no separate Story-text vs Feed-text in the
// Campaign schema.
function StoryStack({ dto }: { dto: PublicPreviewDto }) {
  const pairings = pairAssetsWithText({
    assets: dto.meta?.storyAssets ?? [],
    headlines: dto.meta?.headlines ?? [],
    bodyCopies: dto.meta?.bodyCopy ?? [],
    ctaButton: dto.meta?.ctaButton ?? null,
  });
  // Stage 1 §13.16. Mockup chrome shows the composed location display
  // name (the same string PreviewHeader leads with). Falls back to
  // location.name if displayName isn't populated; the BrandCircle
  // stays driven by brandName so the initial mark is the brand's.
  const pageName = dto.location.displayName || dto.location.name;
  return (
    <div className="space-y-8">
      {pairings.map((p) => (
        <StoryMockup
          key={p.asset.driveFileId}
          asset={p.asset}
          brandName={dto.brand.name}
          pageName={pageName}
          caption={p.headline ?? p.body}
          ctaLabel={p.cta}
        />
      ))}
    </div>
  );
}

function FeedStack({ dto }: { dto: PublicPreviewDto }) {
  const pairings = pairAssetsWithText({
    assets: dto.meta?.feedAssets ?? [],
    headlines: dto.meta?.headlines ?? [],
    bodyCopies: dto.meta?.bodyCopy ?? [],
    ctaButton: dto.meta?.ctaButton ?? null,
  });
  const pageName = dto.location.displayName || dto.location.name;
  return (
    <div className="space-y-8">
      {pairings.map((p) => (
        <FeedMockup
          key={p.asset.driveFileId}
          asset={p.asset}
          brandName={dto.brand.name}
          pageName={pageName}
          bodyCopy={p.body}
          headline={p.headline}
          // Description is a Google-side field, not Meta — the Meta DTO
          // doesn't carry per-Campaign Meta descriptions distinct from
          // body copy. Pass null so the existing FeedMockup omits the
          // descriptor line.
          description={null}
          ctaLabel={p.cta}
        />
      ))}
    </div>
  );
}

function PreviewHeader({ dto }: { dto: PublicPreviewDto }) {
  // Stage 1 §13.9: lead with the composed location.displayName
  // (e.g. "Snap Fitness Barrington" built from clubNamePrefix +
  // franchiseSuburb). Robust regardless of what Account.name happens
  // to be in the source data. The server already falls displayName
  // back to Account.name when either profile field is missing.
  //
  // Brand name lives below the heading as smaller secondary context
  // so the OG-tag unfurl + at-a-glance brand recognition still work.
  // When brand and the rendered display name happen to overlap
  // (clubNamePrefix appears at the start of displayName, which is
  // the common case), we still show it — the brand line is the
  // canonical Account.name and may carry "Australia" / "New Zealand"
  // suffixes that aren't in the compact display name.
  const heading = dto.location.displayName || dto.location.name;
  const showBrandLine =
    dto.brand.name.trim().toLowerCase() !== heading.trim().toLowerCase();
  // Stage 1 §13.4: small badge below the campaign name calling out
  // which Meta ad type the row runs. Only shown when there's actual
  // Meta content on the preview (story or feed assets present) —
  // otherwise the badge would point at empty tabs.
  const hasMetaContent =
    !!dto.meta && (dto.meta.storyAssets.length > 0 || dto.meta.feedAssets.length > 0);
  return (
    <header className="border-b border-slate-200 bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-1">
        {/* §4.2: subtle banner so the franchisee knows this is a
            mockup, not the live ad. */}
        <p className="text-xs uppercase tracking-wide text-amber-700">
          Ad Preview — not yet published
        </p>
        <h1 className="text-2xl font-semibold text-slate-900">{heading}</h1>
        {showBrandLine && (
          <p className="text-sm text-slate-500">{dto.brand.name}</p>
        )}
        <p className="text-sm text-slate-600">{dto.campaign.name}</p>
        {hasMetaContent && dto.meta && <MetaAdTypeBadge adType={dto.meta.adType} />}
      </div>
    </header>
  );
}

// Stage 1 §13.4. Decorative — purely informative for the franchisee.
function MetaAdTypeBadge({
  adType,
}: {
  adType: NonNullable<PublicPreviewDto['meta']>['adType'];
}) {
  if (!adType) return null;
  let icon: string;
  let label: string;
  switch (adType) {
    case 'lead_gen':
      icon = '📩';
      label = 'Meta Lead Gen Ad';
      break;
    case 'traffic':
      icon = '🔗';
      label = 'Website Lead Ad';
      break;
    case 'both':
      icon = '📩';
      label = 'Meta Lead Gen + Website Lead Ad';
      break;
    default: {
      // Defensive — surface unknown enum values rather than render
      // nothing. The cast satisfies the exhaustive-switch narrowing.
      icon = '📣';
      label = `Meta ${String(adType)} Ad`;
    }
  }
  return (
    <p className="pt-1">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
        <span aria-hidden>{icon}</span>
        <span>{label}</span>
      </span>
    </p>
  );
}

function PreviewTabs({
  tabs,
  activeKey,
  onSelect,
}: {
  tabs: ReadonlyArray<{
    key: TabKey;
    label: string;
    count: number | null;
    enabled: boolean;
  }>;
  activeKey: TabKey;
  onSelect: (next: TabKey) => void;
}) {
  return (
    <div className="border-b border-slate-200 sticky top-0 bg-white z-10">
      <nav className="max-w-2xl mx-auto px-4 flex overflow-x-auto" aria-label="Ad format tabs">
        {tabs.map((t) => {
          const active = t.key === activeKey;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={!t.enabled}
              onClick={() => t.enabled && onSelect(t.key)}
              title={!t.enabled ? 'No content for this format' : undefined}
              className={`
                px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap
                ${
                  active
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-900'
                }
                ${!t.enabled ? 'opacity-40 cursor-not-allowed' : ''}
              `}
            >
              {t.label}
              {t.count !== null && (
                <span className="ml-2 text-xs text-slate-400">({t.count})</span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function TabEmpty({ visible, message }: { visible: boolean; message: string }) {
  if (!visible) return null;
  return (
    <p className="text-sm text-slate-500 italic py-8 text-center">{message}</p>
  );
}

function PreviewFooter({ dto }: { dto: PublicPreviewDto }) {
  return (
    <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">
      Created by Kyck • Expires{' '}
      {new Date(dto.expiresAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}
    </footer>
  );
}
